import React, { useContext, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { context } from "utils/Provider";
import { useDatasetsByCubeIdQuery, useDataFromTablesByDatasetIdLazyQuery } from "graphql/types/graphql";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import HeaderSection from "./HeaderSection";
import CardsSection from "./CardSection";
import SqlPreview from "./SQLPreview";
import { useMergeHandler } from "../hooks/useMergeHandler";
import { useCardClickHandler } from "../hooks/useHandleCard";
import DatasetPreviewComponent from "../../upload/components/DatasetPreviewComponent";

import { enqueueSnackbar } from "notistack";
import { useHandlePromote } from "../hooks/useHandlePromote";

interface MergerCardsProps {
  setDatasetId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedDatasetId: string | null;
  outputTableName: string;
  setOutputTableName: React.Dispatch<React.SetStateAction<string>>;
}

const MergerCards: React.FC<MergerCardsProps> = ({
  setDatasetId,
  selectedDatasetId,
  outputTableName,
  setOutputTableName,
}) => {
  const { selectedCube } = useContext(context);

  const {
    loading: datasetLoading,
    error: datasetError,
    data,
  } = useDatasetsByCubeIdQuery({
    variables: { cubeId: selectedCube?.cubeId, includeAlreadyMerged: true },
  });
  const [getDataFromTables, { loading: dataLoading, error: dataError }] = useDataFromTablesByDatasetIdLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const [datasets, setDatasets] = useState(data?.datasetsByCubeId || []);
  const [openedDatasetIds, setOpenedDatasetIds] = useState<string[]>([]);
  const [datasetColumns, setDatasetColumns] = useState<Record<string, string[]>>({});
  const [selectedColumns, setSelectedColumns] = useState<
    Record<
      string,
      { selectedColumns: string[]; joinColumn: string | null; joinWith?: string; joinWithColumn?: string | null }
    >
  >({});
  const [tableOrder, setTableOrder] = useState<string[]>([]);
  const [viewPage, setViewPage] = useState(0);
  const [isReplacingView, setIsReplacingView] = useState(false);
  const viewPageSize = 10;
  const datasetId = openedDatasetIds?.slice(-1)[0];
  const { handleMerge } = useMergeHandler(
    selectedCube?.cubeId || "",
    setOpenedDatasetIds,
    setSelectedColumns,
    setTableOrder,
    setOutputTableName,
    isReplacingView
  );

  const { handlePromote } = useHandlePromote(
    selectedCube?.cubeId || "",
    setOpenedDatasetIds,
    setTableOrder,
    setSelectedColumns,
    setDatasetId
  );

  const handleCardClick = (datasetId: string) => {
    if (openedDatasetIds.includes(datasetId)) {
      const newOpenedIds = openedDatasetIds.filter((id) => id !== datasetId);
      setOpenedDatasetIds(newOpenedIds);
      setTableOrder(tableOrder.filter((id) => id !== datasetId));

      if (selectedDatasetId === datasetId) {
        setDatasetId(null);
      }

      const newSelectedColumns = { ...selectedColumns };
      delete newSelectedColumns[datasetId];
      setSelectedColumns(newSelectedColumns);
    } else {
      useCardClickHandler({
        openedDatasetIds,
        setOpenedDatasetIds,
        setTableOrder,
        setSelectedColumns,
        datasetColumns,
        setDatasetColumns,
        getDataFromTables,
        selectedCube,
      })(datasetId);
      setDatasetId(datasetId);
    }
  };

  React.useEffect(() => {
    setDatasets(data?.datasetsByCubeId || []);
  }, [data]);

  const sortedDatasets = React.useMemo(() => {
    return datasets
      .filter((dataset): dataset is { id: string; name: string } => !!dataset.id && !!dataset.name)
      .sort((a, b) => {
        const indexA = tableOrder.indexOf(a.id);
        const indexB = tableOrder.indexOf(b.id);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return (
          datasets.findIndex((dataset) => dataset.id === a.id) - datasets.findIndex((dataset) => dataset.id === b.id)
        );
      });
  }, [datasets, tableOrder]);

  const validateMergeInputs = () => {
    if (tableOrder.length < 2) {
      enqueueSnackbar("At least two datasets are required for merging.", { variant: "error" });
      return false;
    }

    if (Object.keys(selectedColumns).length === 0) {
      enqueueSnackbar("No columns selected for merging. Please select columns for the datasets.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      return false;
    }

    const invalidJoins = tableOrder.slice(1).some((datasetId) => {
      const config = selectedColumns[datasetId];
      return !config?.joinWith || !config?.joinWithColumn;
    });

    if (invalidJoins) {
      enqueueSnackbar("All non-first datasets must have a valid 'Join With' and 'Join by' selected.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      return false;
    }

    return true;
  };

  const handleMergeWithValidation = () => {
    if (validateMergeInputs()) {
      handleMerge(outputTableName, selectedColumns, tableOrder, sortedDatasets);
    }
  };

  const isPromoteButtonDisabled = openedDatasetIds.length !== 1 || !selectedDatasetId;
  if (datasetLoading) return <CircularProgress />;
  if (datasetError)
    return (
      <MDTypography variant="body2" color="error">
        {`Error fetching datasets: ${datasetError.message}`}
      </MDTypography>
    );

  const cubeName = selectedCube?.name;

  const datasetName =
    datasets?.find((dataset) => dataset.id === openedDatasetIds?.slice(-1)[0])?.name || "Unknown name";

  return (
    <MDBox>
      <HeaderSection
        outputTableName={outputTableName}
        setOutputTableName={setOutputTableName}
        handleMerge={handleMergeWithValidation}
        handlePromote={(name: string) =>
          handlePromote(name, selectedDatasetId!, datasets.find((d) => d.id === selectedDatasetId)?.name || "Unknown")
        }
        loading={dataLoading}
        isPromoteButtonDisabled={isPromoteButtonDisabled}
        selectedCubeId={selectedCube.cubeId}
        viewPage={viewPage}
        setViewPage={setViewPage}
        viewPageSize={viewPageSize}
        setIsReplacingView={setIsReplacingView}
      />
      <CardsSection
        datasets={sortedDatasets}
        openedDatasetIds={openedDatasetIds}
        handleCardClick={handleCardClick}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        datasetColumns={datasetColumns}
        tableOrder={tableOrder}
        isLoading={dataLoading}
        isReplacingView={isReplacingView}
      />
      <DatasetPreviewComponent cubeName={cubeName} datasetId={datasetId} datasetName={datasetName} />
      <SqlPreview selectedColumns={selectedColumns} tableOrder={tableOrder} datasets={sortedDatasets} />
    </MDBox>
  );
};

export default MergerCards;
